import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PasswordInput from '@/components/PasswordInput';
import { LOGIN } from '@/constants/testIds';
import { Sprout } from 'lucide-react';

export default function Login() {
  const { user, login, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (user && typeof user === 'object') return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(username.trim().toLowerCase(), password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#F9F9F6]">
      {/* Left brand panel — minimalistinen: vain ikoni + brändinimi ylhäällä */}
      <div className="hidden lg:flex w-1/2 bg-[#2A4B30] text-white p-12 flex-col">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D6E0D4] text-[#2A4B30] flex items-center justify-center">
            <Sprout size={22} strokeWidth={2} />
          </div>
          <span className="font-heading text-2xl tracking-tight">Herne&nbsp;Tilaukset</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6">
          <div className="space-y-1">
            <p className="text-xs tracking-[0.18em] uppercase text-[#6A6D66] font-semibold">Kirjautuminen</p>
            <h2 className="font-heading text-3xl tracking-tight text-[#1C1C1A]">Tervetuloa takaisin</h2>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-sm text-[#1C1C1A]">Käyttäjätunnus</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid={LOGIN.usernameInput}
                placeholder="esim. admin"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm text-[#1C1C1A]">Salasana</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={LOGIN.passwordInput}
                autoComplete="current-password"
                required
                className="h-11"
                toggleTestId="login-password-toggle"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-[#C05A3C] bg-[#FBEAE5] border border-[#C05A3C]/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid={LOGIN.submitButton}
            className="w-full h-11 bg-[#2A4B30] hover:bg-[#1E3622] text-white rounded-full font-medium"
          >
            {loading ? 'Kirjaudutaan…' : 'Kirjaudu sisään'}
          </Button>
          <p className="text-xs text-[#6A6D66]">Vain valtuutetuille käyttäjille. Pyydä tunnukset ylläpidolta.</p>
        </form>
      </div>
    </div>
  );
}
